/**
 * KeyboardShortcutsSheet — #2324
 *
 * Modal overlay showing all keyboard shortcuts.
 * Triggered by pressing "?" anywhere (when no text input is focused).
 * Also exported so the FleetLayout can render it.
 */

import { useEffect } from "react";

interface Props {
  onClose: () => void;
}

const SECTIONS = [
  {
    title: "Global",
    shortcuts: [
      { keys: ["⌘", "K"],       desc: "Open command palette" },
      { keys: ["?"],             desc: "Show this cheat sheet" },
    ],
  },
  {
    title: "Hive tabs (#2182)",
    shortcuts: [
      { keys: ["⌘", "T"],       desc: "New agent-chat tab" },
      { keys: ["⌘", "W"],       desc: "Close active tab" },
      { keys: ["⌘", "Tab"],     desc: "Next tab" },
      { keys: ["⌘", "⇧", "Tab"], desc: "Previous tab" },
      { keys: ["⌘", "1–9"],     desc: "Jump to nth tab" },
    ],
  },
  {
    title: "Fleet board",
    shortcuts: [
      { keys: ["⌘", "N"],       desc: "New card (opens New Card modal)" },
      { keys: ["j"],             desc: "Move cursor to next card" },
      { keys: ["k"],             desc: "Move cursor to previous card" },
      { keys: ["↵"],             desc: "Open cursor card" },
      { keys: ["/"],             desc: "Focus search box" },
      { keys: ["Esc"],           desc: "Close open card / clear cursor" },
      { keys: ["click header"],  desc: "Collapse / expand column (persisted)" },
    ],
  },
  {
    title: "Chat / agent input",
    shortcuts: [
      { keys: ["⌘", "↵"],       desc: "Send message (textarea)" },
    ],
  },
];

function KbdKey({ k }: { k: string }) {
  return (
    <kbd style={{
      display: "inline-flex", alignItems: "center", justifyContent: "center",
      minWidth: 22, height: 22, padding: "0 5px",
      background: "var(--brand-bg-sunken)",
      border: "1px solid var(--brand-border-strong)",
      borderRadius: "var(--brand-radius-sm)",
      fontSize: "0.72rem", fontWeight: 600,
      color: "var(--brand-text-primary)",
      fontFamily: "var(--brand-font-sans)",
      boxShadow: "0 1px 2px rgba(0,0,0,0.25)",
    }}>
      {k}
    </kbd>
  );
}

export function KeyboardShortcutsSheet({ onClose }: Props) {
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape" || e.key === "?") { e.preventDefault(); onClose(); }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      style={{
        position: "fixed", inset: 0, zIndex: 8000,
        background: "rgba(0,0,0,0.6)", backdropFilter: "blur(2px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 16,
      }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        background: "var(--brand-bg-elevated)",
        border: "1px solid var(--brand-border-strong)",
        borderRadius: "var(--brand-radius-lg)",
        boxShadow: "0 20px 60px rgba(0,0,0,0.55)",
        width: "100%", maxWidth: 500,
        maxHeight: "80vh", overflowY: "auto",
      }}>
        {/* Header */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "14px 18px",
          borderBottom: "1px solid var(--brand-border-subtle)",
        }}>
          <span style={{
            fontWeight: 700, fontSize: "var(--brand-text-base)",
            color: "var(--brand-text-primary)",
            fontFamily: "var(--brand-font-sans)",
          }}>
            Keyboard shortcuts
          </span>
          <button
            onClick={onClose}
            aria-label="Close shortcuts"
            style={{
              background: "transparent", border: "none",
              color: "var(--brand-text-muted)", cursor: "pointer",
              fontSize: "1.1rem", lineHeight: 1,
              padding: "2px 4px", borderRadius: "var(--brand-radius-sm)",
            }}
          >
            ✕
          </button>
        </div>

        {/* Sections */}
        <div style={{ padding: "12px 0" }}>
          {SECTIONS.map((section) => (
            <div key={section.title} style={{ marginBottom: 12 }}>
              <div style={{
                padding: "4px 18px 6px",
                fontSize: "0.62rem", fontWeight: 700, letterSpacing: "0.1em",
                textTransform: "uppercase", color: "var(--brand-text-muted)",
                fontFamily: "var(--brand-font-sans)",
              }}>
                {section.title}
              </div>
              {section.shortcuts.map(({ keys, desc }) => (
                <div
                  key={desc}
                  style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    padding: "6px 18px",
                  }}
                >
                  <span style={{
                    fontSize: "var(--brand-text-sm)",
                    color: "var(--brand-text-secondary)",
                    fontFamily: "var(--brand-font-sans)",
                  }}>
                    {desc}
                  </span>
                  <span style={{ display: "flex", gap: 4, alignItems: "center" }}>
                    {keys.map((k, i) => (
                      <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 3 }}>
                        {i > 0 && <span style={{ color: "var(--brand-text-muted)", fontSize: "0.65rem" }}>+</span>}
                        <KbdKey k={k} />
                      </span>
                    ))}
                  </span>
                </div>
              ))}
            </div>
          ))}
        </div>

        {/* Footer */}
        <div style={{
          padding: "8px 18px",
          borderTop: "1px solid var(--brand-border-subtle)",
          fontSize: "0.62rem", color: "var(--brand-text-muted)",
          fontFamily: "var(--brand-font-sans)",
        }}>
          Press <KbdKey k="?" /> or <KbdKey k="esc" /> to close
        </div>
      </div>
    </div>
  );
}
